import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { industryDetails } from "../data/industries";
import { solutionDetails } from "../data/solutions";
import { articles } from "../data/articles";
import {
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  Check,
  Activity,
  TrendingUp,
  Home,
  AppWindow,
  Trees,
  Hammer,
  Leaf,
  Wind,
  Droplets,
  UtensilsCrossed,
  Car,
  HeartHandshake,
  Cog,
  Briefcase,
} from "lucide-react";

const iconMap = {
  Home,
  AppWindow,
  Trees,
  Hammer,
  Leaf,
  Wind,
  Droplets,
  UtensilsCrossed,
  Car,
  HeartHandshake,
  Cog,
  Briefcase,
};

export default function IndustryPage() {
  const { slug } = useParams();
  const industry = industryDetails[slug];
  if (!industry) return <Navigate to="/" replace />;

  const Icon = iconMap[industry.icon] || Home;
  const relatedArticle = articles.find((a) => a.industry === slug);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[320px] w-[640px] -translate-x-1/2 rounded-full bg-[#d4af37]/12 blur-[120px]" />
          <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8 lg:py-16">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-slate-500 hover:text-[#1e1b4b]">
              <ArrowLeft className="h-3.5 w-3.5" /> All Industries
            </Link>

            <div className="mt-6 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1e1b4b] text-[#d4af37] shadow-[0_12px_30px_-12px_rgba(30,27,75,0.6)]">
                <Icon className="h-6 w-6" />
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
                {industry.eyebrow}
              </div>
            </div>

            <h1 className="mt-6 text-5xl font-medium tracking-tight text-[#1e1b4b] sm:text-6xl">
              BuyerBeacon for <span className="text-slate-400">{industry.name}</span>
            </h1>
            <p className="mt-5 max-w-3xl text-[19px] leading-relaxed text-[#1e1b4b]/80">
              {industry.tagline}
            </p>
            <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-slate-600">
              {industry.description}
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1e1b4b]/92"
              >
                Get a Demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-[#1e1b4b] hover:bg-slate-50">
                See Pricing
              </Link>
            </div>
          </div>
        </section>

        {/* Pain points + Solutions */}
        <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 lg:grid-cols-2 lg:px-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Why it’s hard
              </p>
              <h2 className="mt-2 text-3xl font-medium tracking-tight text-[#1e1b4b]">
                What’s breaking your pipeline.
              </h2>
              <ul className="mt-6 space-y-3">
                {industry.painPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-[#1e1b4b]">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#d4af37]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Recommended Stack
              </p>
              <h2 className="mt-2 text-3xl font-medium tracking-tight text-[#1e1b4b]">
                How BuyerBeacon fixes it.
              </h2>
              <div className="mt-6 space-y-3">
                {industry.solutionStack.map((s) => {
                  const sol = solutionDetails[s.slug];
                  if (!sol) return null;
                  return (
                    <Link
                      key={s.slug}
                      to={`/solutions/${s.slug}`}
                      className="group block rounded-xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#1e1b4b]/20 hover:shadow-md"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#1e1b4b]">{sol.title}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-[#d4af37]" />
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">{s.why}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Signals */}
        {industry.signals && industry.signals.length > 1 && (
          <section className="py-20">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Signals We Watch
                </p>
                <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                  Data points that move the needle.
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-[15px] text-slate-600">
                  Every {industry.name.toLowerCase()} prospect in your audience is scored on these signals — so your team always knows who to reach first.
                </p>
              </div>
              <div className="mt-10 flex flex-wrap justify-center gap-2">
                {industry.signals.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs text-[#1e1b4b]"
                  >
                    <Activity className="h-3 w-3 text-[#d4af37]" />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Use case */}
        <section className="border-t border-slate-200/80 bg-[#faf8f3] py-20">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Outcome Example
            </p>
            <h2 className="mt-3 text-center text-3xl font-medium tracking-tight text-[#1e1b4b] sm:text-4xl">
              {industry.useCase.title}
            </h2>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(30,27,75,0.2)]">
              <TrendingUp className="h-5 w-5 text-[#d4af37]" />
              <p className="mt-4 text-[15px] leading-relaxed text-[#1e1b4b]">
                {industry.useCase.summary}
              </p>
            </div>
          </div>
        </section>

        {/* Agency services callout (only on /industries/agencies) */}
        {industry.services && (
          <section className="border-t border-slate-200/80 bg-white py-20">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Done-For-You Services
                </p>
                <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                  Agency services we run on the data layer.
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-[15px] text-slate-600">
                  Don’t want to staff up? We run social, email, and direct-mail campaigns on top of BuyerBeacon data — as an extension of your team or directly for your clients.
                </p>
              </div>
              <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                {industry.services.map((s) => (
                  <div key={s.name} className="rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1e1b4b] text-[#d4af37]">
                      <Check className="h-4 w-4" />
                    </div>
                    <h3 className="mt-5 text-base font-semibold text-[#1e1b4b]">{s.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related article */}
        {relatedArticle && (
          <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
            <div className="mx-auto max-w-4xl px-5 lg:px-8">
              <div className="flex flex-col items-start gap-6 rounded-2xl border border-slate-200 bg-white p-7 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#d4af37]">
                    Recommended reading
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-[#1e1b4b]">{relatedArticle.title}</h3>
                  <p className="mt-2 max-w-2xl text-sm text-slate-600">{relatedArticle.excerpt}</p>
                </div>
                <Link
                  to={`/resources/${relatedArticle.slug}`}
                  className="group inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1e1b4b]/92"
                >
                  Read article
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </section>
        )}

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
